import React, { useState, useEffect } from "react";
import { formatInTimeZone, utcToZonedTime, zonedTimeToUtc } from "date-fns-tz";
import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  TextField,
  Typography,
} from "@mui/material";
import { LocalizationProvider } from "@mui/x-date-pickers";
import { DateTimePicker } from "@mui/x-date-pickers/DateTimePicker";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import AddIcon from "@mui/icons-material/Add";
import LoadingButton from "@mui/lab/LoadingButton";

import DataTable from "../../../common/DataTable";
import { dateSortComparator } from "../../../../utils/date";
import useAxios from "../../../../hooks/useAxios";
import useAlert from "../../../../hooks/useAlert";
import {
  __getMaintenanceWindows,
  __createMaintenanceWindow,
  __updateMaintenanceWindow,
  __deleteMaintenanceWindow,
} from "../../../../api/sim";

const EST_TZ = "America/New_York";

const windowStatus = (row) => {
  const now = new Date();
  const start = new Date(row.start_date);
  const end = new Date(row.end_date);
  if (now < start) return { label: "Upcoming", color: "info" };
  if (now > end) return { label: "Past", color: "default" };
  return { label: "Active", color: "warning" };
};

const CLIENT = "tmobilej";

const MaintenanceWindows = () => {
  const { setAlertMessage } = useAlert();
  const [windows, setWindows] = useState([]);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingWindow, setEditingWindow] = useState(null);
  const [name, setName] = useState("");
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);

  const [windowsResp, fetchWindows, fetchLoading] = useAxios();
  const [createResp, createApi, createLoading] = useAxios();
  const [updateResp, updateApi, updateLoading] = useAxios();
  const [deleteResp, deleteApi, deleteLoading] = useAxios();

  const fetchAllWindows = () => {
    fetchWindows(__getMaintenanceWindows(CLIENT));
  };

  useEffect(() => {
    fetchAllWindows();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (windowsResp?.status && windowsResp?.data) {
      setWindows(windowsResp.data.data || []);
    }
  }, [windowsResp]);

  useEffect(() => {
    if (!createResp?.status) return;
    setAlertMessage({ type: "success", message: "Maintenance window created" });
    fetchAllWindows();
    handleCloseDialog();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [createResp]);

  useEffect(() => {
    if (!updateResp?.status) return;
    setAlertMessage({ type: "success", message: "Maintenance window updated" });
    fetchAllWindows();
    handleCloseDialog();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [updateResp]);

  useEffect(() => {
    if (!deleteResp?.status) return;
    setAlertMessage({ type: "success", message: "Maintenance window deleted" });
    fetchAllWindows();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [deleteResp]);

  const handleOpenDialog = (row = null) => {
    if (row) {
      setEditingWindow(row);
      setName(row.name ?? "");
      setStartDate(utcToZonedTime(new Date(row.start_date), EST_TZ));
      setEndDate(utcToZonedTime(new Date(row.end_date), EST_TZ));
    } else {
      setEditingWindow(null);
      setName("");
      setStartDate(null);
      setEndDate(null);
    }
    setIsDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setIsDialogOpen(false);
    setEditingWindow(null);
    setName("");
    setStartDate(null);
    setEndDate(null);
  };

  const isDateRangeValid = startDate && endDate && endDate >= startDate;

  const handleSave = async () => {
    if (!startDate || !endDate || !isDateRangeValid) {
      setAlertMessage({
        type: "error",
        message: "A start date and a valid end date are required",
      });
      return;
    }

    const trimmedName = name.trim();
    const payload = {
      client: CLIENT,
      start_date: zonedTimeToUtc(startDate, EST_TZ).toISOString(),
      end_date: zonedTimeToUtc(endDate, EST_TZ).toISOString(),
      name: trimmedName !== "" ? trimmedName : null,
    };

    if (editingWindow) {
      await updateApi({
        ...__updateMaintenanceWindow(editingWindow.id),
        data: payload,
      });
      return;
    }

    await createApi({
      ...__createMaintenanceWindow(),
      data: payload,
    });
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Are you sure you want to delete this maintenance window?"))
      return;
    await deleteApi(__deleteMaintenanceWindow(id));
  };

  const columns = [
    { field: "id", headerName: "ID", flex: 1, sortable: false },
    {
      field: "name",
      headerName: "Name",
      minWidth: 160,
      flex: 3,
      sortable: true,
      valueGetter: (params) => params.row.name || "",
    },
    {
      field: "status",
      headerName: "Status",
      minWidth: 110,
      flex: 1,
      sortable: false,
      renderCell: (params) => {
        const status = windowStatus(params.row);
        return <Chip label={status.label} size="small" color={status.color} />;
      },
    },
    {
      field: "start_date",
      headerName: "Start Date (EST)",
      minWidth: 200,
      flex: 3,
      sortable: true,
      valueGetter: (params) => params.row.start_date || null,
      valueFormatter: (params) =>
        params.value &&
        formatInTimeZone(new Date(params.value), EST_TZ, "MMMM d, y hh:mm a"),
      sortComparator: dateSortComparator,
    },
    {
      field: "end_date",
      headerName: "End Date (EST)",
      minWidth: 200,
      flex: 3,
      sortable: true,
      valueGetter: (params) => params.row.end_date || null,
      valueFormatter: (params) =>
        params.value &&
        formatInTimeZone(new Date(params.value), EST_TZ, "MMMM d, y hh:mm a"),
      sortComparator: dateSortComparator,
    },
    {
      field: "actions",
      headerName: "Actions",
      minWidth: 110,
      sortable: false,
      filterable: false,
      renderCell: (params) => (
        <>
          <IconButton
            size="small"
            onClick={() => handleOpenDialog(params.row)}
            sx={{ mr: 1 }}
          >
            <EditIcon fontSize="small" />
          </IconButton>
          <IconButton
            size="small"
            color="error"
            onClick={() => handleDelete(params.row.id)}
          >
            <DeleteIcon fontSize="small" />
          </IconButton>
        </>
      ),
    },
  ];

  return (
    <Box sx={{ mt: 4, width: "100%" }}>
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        mb={2}
      >
        <Typography variant="h6">Maintenance Windows</Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => handleOpenDialog()}
        >
          Add Window
        </Button>
      </Box>

      <DataTable
        disableColumnFilter
        loading={fetchLoading}
        data={windows}
        columns={columns}
        metaData={{
          page: 1,
          page_size: windows.length || 1,
          total: windows.length,
        }}
        client={true}
      />

      <Dialog
        open={isDialogOpen}
        onClose={handleCloseDialog}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>
          {editingWindow ? "Edit Maintenance Window" : "Add Maintenance Window"}
        </DialogTitle>
        <DialogContent>
          <Box display="flex" flexDirection="column" gap={2} sx={{ mt: 1 }}>
            <TextField
              fullWidth
              label="Name (optional)"
              value={name}
              onChange={(e) => setName(e.target.value)}
            />
            <LocalizationProvider dateAdapter={AdapterDateFns}>
              <DateTimePicker
                label="Start Date (EST)"
                value={startDate}
                onChange={(val) => setStartDate(val)}
              />
              <DateTimePicker
                label="End Date (EST)"
                value={endDate}
                onChange={(val) => setEndDate(val)}
                minDateTime={startDate || undefined}
              />
            </LocalizationProvider>
            {startDate && endDate && !isDateRangeValid && (
              <Typography variant="body2" color="error">
                End date must be on or after start date.
              </Typography>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancel</Button>
          <LoadingButton
            loading={createLoading || updateLoading || deleteLoading}
            variant="contained"
            onClick={handleSave}
            disabled={!startDate || !endDate || !isDateRangeValid}
          >
            Save
          </LoadingButton>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default MaintenanceWindows;
